import { useTranslation } from 'react-i18next';
import { SERVICE_REQUEST_TRANSITIONS, canTransition } from '@/lib/domain';
import { ServiceRequestStatus, ServiceRequestType } from '@/lib/shared-types';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { usePermission } from '@/Hooks/usePermission';
import { formatDateTime, formatDuration } from './shared';

export interface ServiceRequestRow {
  id: string;
  branchId: string;
  type: ServiceRequestType;
  customLabel: string | null;
  status: ServiceRequestStatus;
  note: string | null;
  createdAt: string;
  acceptedAt: string | null;
  escalatedAt: string | null;
  doneAt: string | null;
  area: { id: string; name: string } | null;
  table: { id: string; number: string } | null;
  assignee: { id: string; firstName: string | null; lastName: string | null } | null;
}

export type ServiceCallAction = 'accept' | 'resolve' | 'escalate' | 'cancel';

/**
 * D-08 — one table of what a service call can be told to do next, shared by the
 * queue row and the drawer.
 *
 * Each entry names the state it moves the request **to**, and every call site
 * asks `SERVICE_REQUEST_TRANSITIONS` — the same machine
 * (`@/lib/domain/state-machines.ts`) the service asserts against — rather
 * than a hand-rolled "is it still open". The bug this table exists to prevent is
 * on record: the queue used to offer **Resolve** on a `CREATED` row and the
 * server answered `400 Illegal service request transition: CREATED → DONE`,
 * because `DONE` is only reachable once somebody has accepted. Frame `64:3024`
 * draws all three actions live on a two-minute-old unassigned call, i.e. exactly
 * that state — so the gate is in one place and both surfaces read it.
 *
 * `cancel` shares `service_requests.resolve` with `resolve`: that is what the
 * controller declares.
 */
export const SERVICE_CALL_ACTIONS: ReadonlyArray<{
  action: ServiceCallAction;
  to: ServiceRequestStatus;
  permission: string;
  label: string;
  /** What the toast says once the server has agreed. */
  success: string;
  variant: 'default' | 'outline' | 'danger';
}> = [
  {
    action: 'accept',
    to: ServiceRequestStatus.ACCEPTED,
    permission: 'service_requests.accept',
    label: 'platform.serviceQueue.accept',
    success: 'platform.serviceQueue.accepted',
    variant: 'default',
  },
  {
    action: 'resolve',
    to: ServiceRequestStatus.DONE,
    permission: 'service_requests.resolve',
    label: 'platform.serviceQueue.resolve',
    success: 'platform.serviceQueue.resolved',
    variant: 'default',
  },
  {
    action: 'escalate',
    to: ServiceRequestStatus.ESCALATED,
    permission: 'service_requests.escalate',
    label: 'platform.serviceQueue.escalate',
    success: 'platform.serviceQueue.escalated',
    variant: 'outline',
  },
  {
    action: 'cancel',
    to: ServiceRequestStatus.CANCELLED,
    permission: 'service_requests.resolve',
    label: 'common.cancel',
    success: 'platform.serviceQueue.cancelled',
    variant: 'danger',
  },
];

/** What this request is called: an enum label, or the guest's own words. */
export function requestLabel(row: ServiceRequestRow, t: (key: string) => string): string {
  return row.type === ServiceRequestType.CUSTOM && row.customLabel
    ? row.customLabel
    : t(`platform.serviceQueue.typeLabel.${row.type}`);
}

function personName(person: ServiceRequestRow['assignee']): string | null {
  if (!person) return null;
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return name || null;
}

/**
 * Frame `64:3024` — one service call, opened beside the queue.
 *
 * Rendered through the shared `Dialog` rather than a second modal
 * implementation: it already traps focus, restores it to the row that opened it,
 * owns Escape through the dialog stack and sizes itself in `dvh`. The class
 * override moves it to the trailing edge and makes it full height, which is the
 * only part of a drawer that differs from a modal. A `Drawer` primitive belongs
 * in `components/ui/`; this is not a fork of one, it is the same one placed
 * differently.
 *
 * The frame's dimmer stops at the sidebar. This one does not: everything behind
 * a modal is inert, and dimming only part of it says otherwise.
 *
 * Two things the frame shows are not built. `Priority Indicator: High Priority`
 * has no field on `ServiceRequest` — and `isRush` was removed from kitchen
 * tickets for precisely the reason a priority flag would reintroduce, that an
 * auto-flag silently reorders work. Age is the honest proxy and is here. The
 * `Activity log timeline` is built from the four timestamps the model actually
 * carries; the frame's "System routed warning to assigned section waiter" is not
 * recorded anywhere, and `GET /audit/entity/ServiceRequest/:id` — which does
 * carry the real events — is gated on `reports.view`, which a floor supervisor
 * need not hold.
 */
export function ServiceCallDrawer({
  request,
  now,
  busy,
  onAction,
  onClose,
}: {
  request: ServiceRequestRow | null;
  /** Ticking clock from the queue, so the age here and there never disagree. */
  now: number;
  /** `${action}:${id}` while that action is in flight. */
  busy: string | null;
  onAction: (action: ServiceCallAction, request: ServiceRequestRow) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();

  if (!request) return null;

  const seconds = Math.max(
    0,
    ((request.doneAt ? new Date(request.doneAt).getTime() : now) -
      new Date(request.createdAt).getTime()) /
      1000,
  );
  const open =
    request.status === ServiceRequestStatus.CREATED ||
    request.status === ServiceRequestStatus.ACCEPTED ||
    request.status === ServiceRequestStatus.ESCALATED;

  const facts: Array<{ label: string; value: string }> = [
    {
      label: t('platform.serviceQueue.where'),
      value: [
        request.table
          ? t('platform.serviceQueue.tableNumber', { number: request.table.number })
          : null,
        request.area?.name ?? null,
      ]
        .filter(Boolean)
        .join(' · ') || '—',
    },
    { label: t('platform.serviceQueue.type'), value: requestLabel(request, t) },
    {
      label: t('platform.serviceQueue.assignee'),
      value: personName(request.assignee) ?? t('platform.serviceQueue.unassigned'),
    },
    { label: t('platform.serviceQueue.waiting'), value: formatDuration(seconds) },
  ];

  const timeline = [
    { at: request.createdAt, label: t('platform.serviceQueue.raised') },
    { at: request.acceptedAt, label: t('platform.serviceQueue.statusLabel.ACCEPTED') },
    { at: request.escalatedAt, label: t('platform.serviceQueue.statusLabel.ESCALATED') },
    { at: request.doneAt, label: t('platform.serviceQueue.statusLabel.DONE') },
  ].filter((entry): entry is { at: string; label: string } => Boolean(entry.at));

  const available = SERVICE_CALL_ACTIONS.filter(
    (entry) =>
      can(entry.permission) &&
      canTransition(SERVICE_REQUEST_TRANSITIONS, request.status, entry.to),
  );

  return (
    <Dialog
      open
      onClose={onClose}
      title={requestLabel(request, t)}
      description={t('platform.serviceQueue.request')}
      className="fixed inset-y-0 end-0 h-full max-h-none w-full max-w-xl rounded-none"
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={toneForStatus(request.status)}>
            {t(`platform.serviceQueue.statusLabel.${request.status}`)}
          </Badge>
          {/* Age, coloured past five minutes — the thing the frame's invented
              "High Priority" chip is a proxy for, and the one that is measured. */}
          <span
            className={
              open && seconds > 300
                ? 'text-sm font-medium tabular-nums text-red-600 dark:text-red-400'
                : 'text-sm tabular-nums text-ink-muted'
            }
          >
            {formatDuration(seconds)}
          </span>
        </div>

        <section className="rounded-lg border p-4">
          <h3 className="mb-3 text-sm font-semibold">
            {t('platform.serviceQueue.details')}
          </h3>
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {facts.map((fact) => (
              <div key={fact.label}>
                <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">{fact.label}</dt>
                <dd className="mt-0.5 text-sm">{fact.value}</dd>
              </div>
            ))}
          </dl>
          {request.note && (
            <div className="mt-4 border-t pt-3">
              <dt className="text-xs uppercase tracking-wide rtl:tracking-normal text-ink-muted">
                {t('platform.serviceQueue.note')}
              </dt>
              <dd className="mt-1 text-sm italic">“{request.note}”</dd>
            </div>
          )}
        </section>

        <div className="flex flex-wrap gap-2">
          {available.map((entry) => (
            <Button
              key={entry.action}
              variant={entry.variant}
              disabled={busy === `${entry.action}:${request.id}`}
              onClick={() => onAction(entry.action, request)}
            >
              {t(entry.label)}
            </Button>
          ))}
          {/*
            DONE and CANCELLED have no outgoing edge at all, so the empty row is
            a rule rather than a permission problem — say which, instead of
            leaving a blank.
          */}
          {SERVICE_REQUEST_TRANSITIONS[request.status].length === 0 && (
            <p className="text-sm text-ink-muted">{t('platform.serviceQueue.terminal')}</p>
          )}
        </div>

        <section>
          <h3 className="mb-2 text-sm font-semibold">
            {t('platform.serviceQueue.timeline')}
          </h3>
          <ol className="space-y-2">
            {timeline.map((entry) => (
              <li key={entry.label} className="flex items-baseline gap-2 text-sm">
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent-solid"
                />
                <span className="text-ink-muted tabular-nums">
                  {formatDateTime(entry.at, i18n.language)}
                </span>
                <span>{entry.label}</span>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </Dialog>
  );
}
